import { Card } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { ModerationAction } from "@/src/components/ModerationAction";
import { PostStatusChip } from "@/src/components/PostStatusChip";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import type { AdminPost } from "@/src/pages/Posts/posts.type";

type PostActionsProps = {
  post: AdminPost;
  isPending: boolean;
  onApprove: (_id: number) => void;
  onReject: (_id: number) => void;
  onDelete: (_id: number) => void;
};

/**
 * Ce qu'on peut faire d'une publication, et ce qui a déjà été décidé.
 *
 * ## Deux gestes qui se défont, un qui ne se défait pas
 *
 * Publier et retirer sont réversibles — l'un annule l'autre, et l'auteur est
 * notifié dans les deux sens. **Effacer ne l'est pas** : cascades sur les
 * photos, les cœurs et les commentaires, plus les objets S3. Il est donc en
 * rouge et à l'écart, comme sur la fiche d'un événement.
 *
 * ⚠️ **`scanning` et `pending_review` ne sont pas proposés** : ce sont des états
 * que la machine attribue. Y renvoyer une publication ne relancerait aucun
 * classement — aucune reprise n'est planifiée — et elle y resterait bloquée. Le
 * serveur refuse en 400 ; un bouton qui ne peut qu'échouer vaut moins que pas de
 * bouton.
 *
 * ## L'historique est ici, pas dans un coin de la fiche
 *
 * Qui a tranché, et quand : c'est la première question qu'on se pose avant de
 * défaire la décision de quelqu'un d'autre.
 */
export function PostActions({
  post,
  isPending,
  onApprove,
  onReject,
  onDelete,
}: PostActionsProps) {
  const t = useTranslations("posts");
  const tm = useTranslations("moderation");
  const { locale } = useLocale();

  const decideLe =
    post.moderatedAt === null
      ? null
      : new Intl.DateTimeFormat(locale, {
          dateStyle: "long",
          timeStyle: "short",
        }).format(new Date(post.moderatedAt));

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-sm font-semibold tracking-wide uppercase">
          {tm("actions")}
        </h2>

        <PostStatusChip status={post.moderationStatus} />
      </div>

      {decideLe ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-default p-3 text-xs text-default-foreground">
          <span>{t("moderated-on", { date: decideLe })}</span>
          <span>{tm("decided-by")}</span>
          {/* `null` quand le compte du modérateur a été supprimé depuis : la
              décision, elle, reste. */}
          <AccountBadge account={post.moderatedBy} isLinked />
        </div>
      ) : (
        <p className="text-sm text-pretty text-muted">
          {t("not-moderated-yet")}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <ModerationAction
          confirmBody={t("confirm-approve-body", {
            author: post.author.username,
          })}
          confirmLabel={t("approve")}
          confirmTitle={t("confirm-approve-title")}
          isDisabled={post.moderationStatus === "approved"}
          isPending={isPending}
          label={t("approve")}
          onConfirm={() => onApprove(post.id)}
          pendingLabel={t("approving")}
        />

        <ModerationAction
          confirmBody={t("confirm-reject-body", {
            author: post.author.username,
          })}
          confirmLabel={t("reject")}
          confirmTitle={t("confirm-reject-title")}
          isDisabled={post.moderationStatus === "rejected"}
          isPending={isPending}
          label={t("reject")}
          onConfirm={() => onReject(post.id)}
          pendingLabel={t("rejecting")}
        />

        <div className="ml-auto">
          <ModerationAction
            confirmBody={t("confirm-delete-body", {
              author: post.author.username,
            })}
            confirmLabel={t("delete")}
            confirmTitle={t("confirm-delete-title")}
            isPending={isPending}
            label={t("delete")}
            onConfirm={() => onDelete(post.id)}
            pendingLabel={t("deleting")}
            tone="danger"
          />
        </div>
      </div>
    </Card>
  );
}
